import type { Locale } from './locale';

/** Texto citado com as aspas da língua: «Guardar» (pt) e “Save” (en). */
export function quote(locale: Locale, s: string): string {
  return locale === 'en' ? '“' + s + '”' : '«' + s + '»';
}

/** Forma do plural pelas regras da língua (Intl.PluralRules). */
export function plural(locale: Locale, n: number, forms: { one: string; other: string }): string {
  return new Intl.PluralRules(locale).select(n) === 'one' ? forms.one : forms.other;
}

/** Data na língua: «12 de outubro de 2026» / «October 12, 2026». */
export function formatDate(
  locale: Locale,
  date: Date | number | string,
  opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' },
): string {
  return new Date(date).toLocaleDateString(locale, opts);
}

export function formatNumber(locale: Locale, n: number, opts?: Intl.NumberFormatOptions): string {
  return n.toLocaleString(locale, opts);
}

/** Dia e hora curtos: «12 out., 14:30» / «Oct 12, 2:30 PM». */
export function formatDateTime(locale: Locale, date: Date | number | string): string {
  return new Date(date).toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Mês por extenso: «outubro de 2026» / «October 2026». */
export function formatMonth(locale: Locale, year: number, month1: number): string {
  return new Date(year, month1 - 1, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });
}

const RELATIVE: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/** Há quanto tempo: «há 4 dias» / «4 days ago»; menos de um minuto: «agora» / «now». */
export function formatRelative(
  locale: Locale,
  date: Date | number | string,
  now: Date | number = Date.now(),
): string {
  const seconds = Math.round((new Date(date).getTime() - new Date(now).getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of RELATIVE)
    if (Math.abs(seconds) >= size) return rtf.format(Math.trunc(seconds / size), unit);
  return rtf.format(0, 'second');
}
